// Export der Eigentümerliste nach Feldgruppen (Handoff Eigentümer §6.3) – geschrieben
// über die bestehenden lib/export-Funktionen (exportCsv / exportXlsx).

import type { ExportColumn, ExportConfig } from '../export/types';
import { deDatum } from '../immobilien/objektDetail';
import { EIG_STATUS, PORTAL_ANZEIGE, type EigZeile } from './liste';
import { ansprechpartnerZusatz, type EigentuemerNameFelder } from './anzeige';
import { RHYTHMEN } from './sepaMandat';
import { adressText, artLabel, hatAbweichendeAdresse, POST_ARTEN, postPflicht, VERSANDWEGE, versandweg, type Beteiligter } from './beteiligte';

export type ExportGruppe = 'stammdaten' | 'adresse' | 'bank' | 'objekte' | 'konto' | 'portal' | 'zustellung';

export const EXPORT_GRUPPEN: readonly { key: ExportGruppe; label: string; standard: boolean }[] = [
  { key: 'stammdaten', label: 'Stammdaten & Kontakt', standard: true },
  { key: 'adresse', label: 'Adresse (getrennt)', standard: true },
  { key: 'bank', label: 'Bank & SEPA', standard: false },
  { key: 'objekte', label: 'Objekte, Einheiten & Rollen', standard: true },
  { key: 'konto', label: 'Konto, Hausgeld & Vergütung', standard: false },
  { key: 'portal', label: 'Portal-Status', standard: false },
  { key: 'zustellung', label: 'Zustellung & Vertreter', standard: false },
];

export const standardGruppen = (): Set<ExportGruppe> => new Set(EXPORT_GRUPPEN.filter(g => g.standard).map(g => g.key));

const SPALTEN: Record<ExportGruppe, readonly ExportColumn[]> = {
  stammdaten: [
    { key: 'name', label: 'Name' },
    { key: 'ansprechpartner', label: 'Ansprechpartner' },
    { key: 'anrede', label: 'Anrede' },
    { key: 'vorname', label: 'Vorname' },
    { key: 'nachname', label: 'Nachname' },
    { key: 'firma_name', label: 'Firma' },
    { key: 'email', label: 'E-Mail' },
    { key: 'telefon', label: 'Telefon' },
    { key: 'mobil', label: 'Mobil' },
    { key: 'kundennummer', label: 'Kundennummer' },
    { key: 'status', label: 'Status' },
  ],
  adresse: [
    { key: 'strasse', label: 'Straße' },
    { key: 'hausnummer', label: 'Nr.' },
    { key: 'plz', label: 'PLZ' },
    { key: 'stadt', label: 'Stadt' },
  ],
  bank: [
    { key: 'bankverbindung', label: 'IBAN / Bankverbindung' },
    { key: 'kontoinhaber', label: 'Kontoinhaber' },
    { key: 'bic', label: 'BIC' },
    { key: 'auszahlung_rhythmus', label: 'Auszahlungsrhythmus' },
    { key: 'sepa_mandatsreferenz', label: 'SEPA-Mandatsreferenz' },
    { key: 'sepa_mandatsdatum', label: 'Mandat erteilt am' },
    { key: 'glaeubiger_id', label: 'Gläubiger-ID' },
    { key: 'steuer_id', label: 'Steuer-ID' },
    { key: 'steuer_nr', label: 'Steuer-Nr.' },
    { key: 'finanzamt', label: 'Finanzamt' },
  ],
  objekte: [
    { key: 'rollen', label: 'Rollen' },
    { key: 'objekte', label: 'Objekte' },
    { key: 'einheiten', label: 'Einheiten' },
    { key: 'anzahl_einheiten', label: 'Anzahl Einheiten' },
    { key: 'flaeche', label: 'Fläche (m²)' },
    { key: 'vertragsbeginn', label: 'Vertragsbeginn' },
    { key: 'vertragsende', label: 'Vertragsende' },
  ],
  konto: [
    { key: 'hausgeld', label: 'Hausgeld / Monat' },
    { key: 'miete', label: 'Miete / Monat' },
    { key: 'verguetung', label: 'Verwaltervergütung / Monat' },
    { key: 'auszahlung', label: 'Auszahlung / Monat' },
    { key: 'saldo', label: 'Saldo' },
  ],
  portal: [{ key: 'portal', label: 'Portal-Status' }],
  zustellung: [
    { key: 'versandweg', label: 'Versandweg' },
    { key: 'textform', label: 'Einwilligung Textform' },
    { key: 'post_pflicht', label: 'Immer per Post' },
    { key: 'zustelladresse', label: 'Abweichende Zustelladresse' },
    { key: 'zustellvertreter', label: 'Zustellvertreter' },
    { key: 'miteigentuemer', label: 'Miteigentümer' },
    { key: 'vertreter', label: 'Vertreter' },
  ],
};

/** Exportgruppen, für die zusätzliche Tabellen geladen werden müssen. */
export const brauchtBeteiligte = (gruppen: ReadonlySet<ExportGruppe>): boolean => gruppen.has('zustellung');

/** Spalten in fester Reihenfolge der Gruppen. */
export function exportSpalten(gruppen: ReadonlySet<ExportGruppe>): ExportColumn[] {
  return EXPORT_GRUPPEN.filter(g => gruppen.has(g.key)).flatMap(g => SPALTEN[g.key]);
}

export type EigExportRoh = EigentuemerNameFelder & { id: string } & Record<string, unknown>;

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();
const betrag = (n: number): string => (n ? n.toFixed(2).replace('.', ',') : '');

/** Flache Exportzeile aus Listenzeile + vollständigem Datensatz. */
export function exportZeile(z: EigZeile, roh: EigExportRoh, beteiligte: readonly Beteiligter[] = []): Record<string, string> {
  const w = z.werte;
  const weg = versandweg(roh.versandweg, z.portal === 'aktiv', z.email);
  const einw = text(roh.textform_einwilligung_am);
  const post = postPflicht(roh.post_pflicht);
  const mit = beteiligte.filter(b => b.typ === 'miteigentuemer');
  const vtr = beteiligte.filter(b => b.typ === 'vertreter');
  const zustell = { co: text(roh.zustell_co), strasse: text(roh.zustell_strasse), hausnummer: text(roh.zustell_hausnummer), plz: text(roh.zustell_plz), stadt: text(roh.zustell_stadt) };
  const einheiten = z.zuordnungen.flatMap(x => (x.ganzesObjekt && x.einheiten.length === 0 ? [`${x.objektName} (ganzes Objekt)`] : x.einheiten.map(e => `${x.objektName} ${e.bezeichnung}`.trim())));
  return {
    name: z.name,
    ansprechpartner: ansprechpartnerZusatz(roh),
    anrede: text(roh.anrede),
    vorname: text(roh.vorname),
    nachname: text(roh.nachname),
    firma_name: text(roh.firma_name),
    email: z.email,
    telefon: z.telefon,
    mobil: text(roh.mobil),
    kundennummer: text(roh.kundennummer),
    status: EIG_STATUS[z.status].label,
    strasse: text(roh.strasse),
    hausnummer: text(roh.hausnummer),
    plz: text(roh.plz),
    stadt: text(roh.stadt),
    bankverbindung: text(roh.bankverbindung),
    sepa_mandatsreferenz: text(roh.sepa_mandatsreferenz),
    glaeubiger_id: text(roh.glaeubiger_id),
    steuer_id: text(roh.steuer_id),
    steuer_nr: text(roh.steuer_nr),
    finanzamt: text(roh.finanzamt),
    kontoinhaber: text(roh.kontoinhaber),
    bic: text(roh.bic),
    auszahlung_rhythmus: RHYTHMEN.find(r => r.key === roh.auszahlung_rhythmus)?.label ?? '',
    sepa_mandatsdatum: text(roh.sepa_mandatsdatum) ? deDatum(text(roh.sepa_mandatsdatum).slice(0, 10)) : '',
    rollen: w.rollenL,
    objekte: [...new Set(z.zuordnungen.map(x => x.objektName))].join(', '),
    einheiten: einheiten.join(', '),
    anzahl_einheiten: w.nEinheiten ? String(w.nEinheiten) : '',
    flaeche: w.flaeche ? String(Math.round(w.flaeche)) : '',
    vertragsbeginn: z.beginn ? deDatum(z.beginn) : '',
    vertragsende: z.endetAm ? deDatum(z.endetAm) : '',
    hausgeld: betrag(w.hgM + w.hgExt),
    miete: betrag(w.miete),
    // nicht hinterlegt bzw. ohne Buchhaltung bleibt die Zelle leer statt „0“
    verguetung: w.vergM === null ? '' : betrag(w.vergM),
    auszahlung: w.verwaltet ? betrag(w.ausz) : '',
    saldo: z.saldo === null ? '' : z.saldo.toFixed(2).replace('.', ','),
    portal: PORTAL_ANZEIGE[z.portal].label,
    versandweg: `${VERSANDWEGE.find(v => v.key === weg.weg)?.label ?? ''}${weg.gespeichert ? '' : ' (Vorschlag)'}`,
    textform: einw ? `eingewilligt am ${deDatum(einw.slice(0, 10))}` : 'nicht erteilt',
    post_pflicht: POST_ARTEN.filter(p => post[p.key]).map(p => p.label).join(', '),
    zustelladresse: hatAbweichendeAdresse(zustell) ? adressText(zustell) : '',
    zustellvertreter: beteiligte.find(b => b.zustellvertreter)?.name ?? '',
    miteigentuemer: mit.map(b => `${b.name} (${artLabel(b.art)}${b.anteil !== null ? `, ${String(b.anteil).replace('.', ',')} %` : ''})`).join('; '),
    vertreter: vtr.map(b => `${b.name} (${artLabel(b.art)}${b.gueltig_bis ? `, bis ${deDatum(b.gueltig_bis.slice(0, 10))}` : ''})`).join('; '),
  };
}

export function exportKonfig(
  zeilen: readonly EigZeile[], roh: ReadonlyMap<string, EigExportRoh>, gruppen: ReadonlySet<ExportGruppe>, heute: string,
  beteiligte: ReadonlyMap<string, readonly Beteiligter[]> = new Map(),
): ExportConfig {
  const daten = zeilen.flatMap(z => {
    const r = roh.get(z.id);
    return r ? [exportZeile(z, r, beteiligte.get(z.id) ?? [])] : [];
  });
  return {
    title: 'Eigentümer',
    fileName: `eigentuemer_${heute}`,
    columns: exportSpalten(gruppen),
    data: daten,
    stats: { items: [
      { label: 'Eigentümer', value: daten.length },
      { label: 'Aktiv', value: zeilen.filter(z => z.status !== 'ehemalig').length },
      { label: 'Ehemalig', value: zeilen.filter(z => z.status === 'ehemalig').length },
    ] },
  };
}
